import React from 'react';
import { Card, Row, Col, Form, Badge } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { numericKeydownHandler } from '../../../utilities/common';
import { formatNumber } from '../../reports/reportHelper';
import { formatCo2eFactor } from '../scenarioConfig';

const TemperatureReductionForm = ({ baseline, mode }) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const elec = baseline?.data?.electricity;
  const gas  = baseline?.data?.gas;

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div className="font-weight-bold mb-3">
            {t('CarbonView Data')}
          </div>

          <Row>
            <Col md={3}>
              <div className="text-muted small">
                {t('HVAC Electricity')}
              </div>
              <div className="font-weight-medium">
                {elec
                  ? `${formatNumber(elec.consumption)} ${elec.unit ?? ''}/year`
                  : '...'}
              </div>
            </Col>

            <Col md={3}>
              <div className="text-muted small">
                {t('HVAC Gas')}
              </div>
              <div className="font-weight-medium">
                {gas
                  ? `${formatNumber(gas.consumption)} ${gas.unit ?? ''}/year`
                  : '...'}
              </div>
            </Col>

            <Col md={3}>
              <div className="text-muted small">
                {t('Electricity EF')}
              </div>
              <div className="font-weight-medium">
                {elec
                  ? `${formatCo2eFactor(elec.emissionFactor)} tCO₂e/${elec.unit ?? ''}`
                  : '...'}
              </div>
            </Col>

            <Col md={3}>
              <div className="text-muted small">
                {t('Gas EF')}
              </div>
              <div className="font-weight-medium">
                {gas
                  ? `${formatCo2eFactor(gas.emissionFactor)} tCO₂e/${gas.unit ?? ''}`
                  : '...'}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="font-weight-bold">
              {t('Temperature Adjustment')}
            </div>
            <Badge variant="success">
              {t('Required')}
            </Badge>
          </div>

          <div className="text-muted small mb-4">
            {t('Define expected efficiency gain from 1°C reduction')}
          </div>

          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t('Electricity Tariff (£/kWh)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.18')}
                  {...register('electricityTariff')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.electricityTariff}
                />
                <Form.Text className="text-muted">
                  {t('Current electricity cost per kWh')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.electricityTariff?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t('Gas Tariff (£/kWh)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.06')}
                  {...register('gasTariff')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.gasTariff}
                />
                <Form.Text className="text-muted">
                  {t('Current gas cost per kWh')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.gasTariff?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t('Efficiency Gain per °C (%)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 8')}
                  {...register('efficiencyGain')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.efficiencyGain}
                />
                <Form.Text className="text-muted">
                  {t('Typical range: 6–10% savings per degree (Celsius)')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.efficiencyGain?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default TemperatureReductionForm;
